import { useFlags } from "launchdarkly-react-client-sdk";

import { ApiLearnerManualGoalSyncStatusChoices } from "@api/graphql/types-and-hooks";

/**
 * CR learning tree review (INT-221).
 *
 * The flag lives only in the portal: when it's on, every import the portal
 * starts asks FDM for the review (`learningTreeReview: true`). FDM decides the
 * rest — a client with a locked structure always syncs from it, whatever the
 * flag says, and custom-workflow orgs keep today's flow.
 *
 * TODO: once `cr-learning-tree-review` exists in LaunchDarkly, run
 * `npm run flaggen` and read it through `useFlags<FeatureFlags>()`.
 */
export const LEARNING_TREE_REVIEW_FLAG = "cr-learning-tree-review";

export const useLearningTreeReviewEnabled = (): boolean => {
  const flags = useFlags<Record<string, unknown>>();
  return flags[LEARNING_TREE_REVIEW_FLAG] === true;
};

/**
 * Extra `startManualSyncWorkflow` variables. Empty when the flag is off, so the
 * call is exactly today's.
 */
export const learningTreeReviewVariables = (enabled: boolean): { learningTreeReview?: true } =>
  enabled ? { learningTreeReview: true } : {};

/**
 * Whether a learner's EHR sync has finished successfully. Besides COMPLETED,
 * the learning tree review settles on AWAITING_REVIEW (structure read, waits
 * for a person to lock it) and HAS_WAITING_ITEMS (imported; new items wait).
 * Treating only COMPLETED as done would keep pollers spinning forever.
 */
export const isSyncDone = (status: ApiLearnerManualGoalSyncStatusChoices | null | undefined): boolean =>
  status === ApiLearnerManualGoalSyncStatusChoices.Completed ||
  status === ApiLearnerManualGoalSyncStatusChoices.AwaitingReview ||
  status === ApiLearnerManualGoalSyncStatusChoices.HasWaitingItems;
